import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  intakeApi, progressText, localProblem, sectionsWithFields, fieldDone, firstOpenIndex, canTbd, isShown, draftFromValue,
} from '../utils/jobIntake';
import { FieldInput } from '../components/JobFieldInputs';

const AUTOSAVE_MS = 900;
const LOCKED = ['generating', 'ready_for_review', 'approved'];
const HINTS = {
  employment_type: 'Contract length and extension are shown only for Contract, Contract-to-hire and Part-time.',
  work_mode: '“Allowed geographies” appears only when Remote is selected.',
  relocation: 'Relocation assistance appears only when relocation is OK.',
};
export const MY_JOBS = { pathname: '/jobs', state: {} };
// Inputs that take a full row of the card (lists / long text); the rest sit 2–3 per row
const WIDE_INPUTS = ['skills', 'chips', 'locations', 'textarea', 'tech_screen', 'interview_rounds'];

export function agoText(ts, now = Date.now()) {
  if (!ts) return '';
  const s = Math.max(0, Math.round((now - ts) / 1000));
  return s < 60 ? `Autosaved · ${s} s ago` : `Autosaved · ${Math.round(s / 60)} min ago`;
}

// One field card (frame 02): number, label, TBD toggle, "✓ Saved", the inputs, warnings and hints.
function FieldCard({ field, draft, answer, state, warnings, ctx, locked, payHint, onChange, onToggleTbd }) {
  const problem = localProblem(field, draft, ctx);
  const tbd = answer && answer.status === 'tbd';
  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 flex flex-col min-h-0 max-h-full" data-testid={`card-${field.key}`}>
      <div className="flex items-center gap-3">
        <span className="min-w-[24px] h-6 px-1.5 rounded-md bg-blue-50 text-brand-blue text-xs font-bold flex items-center justify-center">{field.no}</span>
        <h3 className="flex-1 text-base font-bold text-gray-900">
          {field.label}{field.approval_required ? ' *' : ''}
        </h3>
        {canTbd(field) && !locked && (
          <button type="button" onClick={onToggleTbd} aria-pressed={!!tbd} title={tbd ? 'Click to undo TBD' : 'Mark TBD — Jessica will ask'}
            className={`px-3 py-1 rounded-full border text-xs font-bold ${tbd ? 'bg-amber-100 border-amber-300 text-amber-800' : 'border-gray-200 text-gray-500 hover:border-amber-300'}`}>
            TBD
          </button>
        )}
        <span className="w-24 text-right text-xs font-semibold" data-testid={`save-${field.key}`}>
          {state === 'saving' ? <span className="text-gray-400">Saving…</span>
            : state === 'saved' || (fieldDone(answer) && !state) ? <span className="text-brand-green">✓ Saved</span>
              : state ? <span className="text-red-600">Not saved</span> : null}
        </span>
      </div>
      {field.approval_required && canTbd(field) && <p className="text-[11px] text-gray-400 mt-1 ml-9">Required for approval — you can mark it TBD for now.</p>}
      {!canTbd(field) && <p className="text-[11px] text-gray-400 mt-1 ml-9">Required — the job can't be approved without it.</p>}
      <p className="text-sm text-gray-600 mt-2 mb-3" data-testid="field-question">{field.question}</p>
      {payHint && <p className="text-xs text-brand-dark bg-blue-50 rounded-lg px-3 py-2 mb-3" data-testid="pay-hint">{payHint}</p>}
      <div className="flex-1 min-h-0 overflow-y-auto pr-1" data-testid="card-body">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-4 gap-y-3">
          {field.inputs.map((inp) => isShown(inp, draft, ctx) && (
            <div key={inp.name} className={WIDE_INPUTS.includes(inp.kind) ? 'md:col-span-2 xl:col-span-3' : ''}>
              <FieldInput input={inp} value={draft} onChange={onChange} disabled={locked} ctx={ctx} />
            </div>
          ))}
        </div>
      {field.key === 'work_authorization' && draft.option === 'c' && (
        <p className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">This job will go to HR review before it can be approved.</p>
      )}
      {(warnings || []).map((w) => (
        <p key={w} role="alert" className="mt-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">{w}</p>
      ))}
      {state && !['saving', 'saved'].includes(state) && <p className="mt-3 text-sm text-red-600">{state}</p>}
      {!state && problem && !fieldDone(answer) && <p className="mt-3 text-xs text-gray-500">{problem}</p>}
      {HINTS[field.key] && <p className="mt-3 text-xs text-gray-400">{HINTS[field.key]}</p>}
      </div>
    </div>
  );
}

function JobIntakeFormPage() {
  const { intakeId } = useParams();
  const navigate = useNavigate();
  const [spec, setSpec] = useState(null);
  const [options, setOptions] = useState({});
  const [job, setJob] = useState(null);
  const [answers, setAnswers] = useState({});
  const [drafts, setDrafts] = useState({});
  const [cursor, setCursor] = useState(0);             // the one field on screen (index over all form fields)
  const [saveState, setSaveState] = useState({});      // key → 'saving' | 'saved' | error text
  const [warnings, setWarnings] = useState({});        // key → [text]
  const [savedAt, setSavedAt] = useState(null);
  const [, setTick] = useState(0);
  const [skillSuggestions, setSkillSuggestions] = useState({ title: null, skills: [] });
  const [error, setError] = useState('');
  const [continuing, setContinuing] = useState(false);
  const timersRef = useRef({});                         // key → { timer, value }

  useEffect(() => {
    (async () => {
      try {
        const [fields, data, opts] = await Promise.all([intakeApi('/fields'), intakeApi(`/${intakeId}`), intakeApi('/options').catch(() => ({}))]);
        setSpec(fields);
        setOptions(opts || {});
        setJob(data);
        setAnswers(data.answers || {});
        setDrafts(Object.fromEntries(Object.entries(data.answers || {}).map(([k, a]) => [k, draftFromValue(k, a.value)])));
        setWarnings(Object.fromEntries(Object.entries(data.answers || {}).map(([k, a]) => [k, a.warnings || []])));
        const open = fields.fields[firstOpenIndex(fields, data.answers)];
        setCursor(Math.max(0, sectionsWithFields(fields).flatMap((s) => s.fields).indexOf(open)));
      } catch (e) {
        setError(e.status === 404 ? 'This job was not found.' : e.message);
      }
    })();
    const timers = timersRef.current;
    const tick = setInterval(() => setTick((t) => t + 1), 5000);
    return () => { clearInterval(tick); Object.values(timers).forEach((t) => clearTimeout(t.timer)); };
  }, [intakeId]);

  const title = ((answers.job_title || {}).value || {}).title || ((drafts.job_title || {}).title) || '';
  const sections = sectionsWithFields(spec);
  const flat = sections.flatMap((s) => s.fields);
  const field = flat[Math.min(cursor, Math.max(flat.length - 1, 0))];
  const section = sections.find((s) => s.fields.includes(field));
  const showsSkills = !!field && ['primary_skills', 'secondary_skills'].includes(field.key);

  // Suggested skill chips once the title is known (company history first, else Claude Haiku — cached per title)
  useEffect(() => {
    if (!showsSkills || !title.trim() || skillSuggestions.title === title) return undefined;
    let alive = true;
    intakeApi(`/suggest/skills?title=${encodeURIComponent(title)}`)
      .then((r) => { if (alive) setSkillSuggestions({ title, skills: r.skills || [] }); })
      .catch(() => { if (alive) setSkillSuggestions({ title, skills: [] }); });
    return () => { alive = false; };
  }, [showsSkills, title, skillSuggestions.title]);

  const save = useCallback(async (key, status, value) => {
    delete timersRef.current[key];
    setSaveState((s) => ({ ...s, [key]: 'saving' }));
    try {
      const res = await intakeApi(`/${intakeId}/fields/${key}`, { method: 'PUT', body: { status, value, source: 'form' } });
      setAnswers((a) => ({ ...a, [key]: { status: res.status, value: res.value, display_text: res.display_text } }));
      setWarnings((w) => ({ ...w, [key]: res.warnings || [] }));
      setJob((j) => ({ ...j, progress: res.progress, hr_review_required: res.hr_review_required }));
      setSaveState((s) => ({ ...s, [key]: 'saved' }));
      setSavedAt(Date.now());
    } catch (e) {
      setSaveState((s) => ({ ...s, [key]: e.message }));
    }
  }, [intakeId]);

  const clear = useCallback(async (key) => {
    setAnswers((a) => { const n = { ...a }; delete n[key]; return n; });
    try {
      const res = await intakeApi(`/${intakeId}/fields/${key}`, { method: 'DELETE' });
      setJob((j) => ({ ...j, progress: res.progress }));
      setSavedAt(Date.now());
    } catch (e) {
      setSaveState((s) => ({ ...s, [key]: e.message }));
    }
  }, [intakeId]);

  if (error) return <div className="p-10 text-center text-red-600">{error}</div>;
  if (!spec || !job || !field) return <div className="p-10 text-center text-gray-500">Loading…</div>;

  const locked = LOCKED.includes(job.status);
  const et = answers.employment_type || {};
  const ctx = {
    options, halfHours: spec.half_hours, geoAttribution: spec.geo_attribution, jobTitle: title,
    employment_type: et.status === 'answered' ? (et.value || {}).type : null,
    skillSuggestions: skillSuggestions.title === title ? skillSuggestions.skills : [],
    primarySkillNames: (((answers.primary_skills || {}).value || {}).skills || []).map((s) => s.name),
  };
  const progress = job.progress || { done: 0, total: spec.total };

  const onChange = (field) => (value) => {
    setDrafts((d) => ({ ...d, [field.key]: value }));
    if ((answers[field.key] || {}).status === 'tbd') clear(field.key);           // typing in a TBD field clears TBD
    clearTimeout((timersRef.current[field.key] || {}).timer);
    if (!localProblem(field, value, ctx)) {
      timersRef.current[field.key] = { value, timer: setTimeout(() => save(field.key, 'answered', value), AUTOSAVE_MS) };
    } else {
      delete timersRef.current[field.key];
      setSaveState((s) => ({ ...s, [field.key]: '' }));
    }
  };
  const toggleTbd = (field) => () => {
    clearTimeout((timersRef.current[field.key] || {}).timer);
    delete timersRef.current[field.key];
    if ((answers[field.key] || {}).status === 'tbd') clear(field.key);           // TBD off → unanswered again
    else save(field.key, 'tbd', null);
  };
  const flush = async () => {                                                   // "Save & exit": send pending edits now
    const pending = Object.entries(timersRef.current);
    pending.forEach(([, t]) => clearTimeout(t.timer));
    await Promise.all(pending.map(([k, t]) => save(k, 'answered', t.value)));
  };
  const continueWithJessica = async () => {
    setContinuing(true);
    try {
      await flush();
      if (job.status === 'form') await intakeApi(`/${intakeId}/complete-form`, { method: 'POST' });
      navigate(`/jobs/intake/${intakeId}/call`);
    } catch (e) {
      setError(e.message);
      setContinuing(false);
    }
  };
  const payHint = !ctx.employment_type ? 'Answer 3. Employment type first — the pay range depends on it.'
    : ctx.employment_type === 'full_time' ? 'Annual base salary (full-time), plus any bonus or equity.'
      : 'Hourly rate (contract / contract-to-hire / part-time).';
  const done = (f) => fieldDone(answers[f.key]);
  const missing = flat.filter((f) => !done(f));
  const pending = !!timersRef.current[field.key];                              // a valid edit not yet autosaved
  const canNext = done(field) || pending;
  const lastField = cursor === flat.length - 1;
  const goTo = (f) => setCursor(flat.indexOf(f));
  const next = async () => {
    const t = timersRef.current[field.key];
    if (t) {                                                                    // save the edit now, then move on
      clearTimeout(t.timer);
      await save(field.key, 'answered', t.value);
    }
    if (!lastField) setCursor(cursor + 1);                                      // after a section's last field: next section
  };
  const onKeyDown = (e) => {                                                     // Enter = Next (inputs that use Enter, e.g. skills, keep it)
    if (e.key !== 'Enter' || e.shiftKey || e.defaultPrevented || ['TEXTAREA', 'BUTTON'].includes(e.target.tagName)) return;
    if (canNext && !lastField) { e.preventDefault(); next(); }
  };
  const inSection = section.fields.indexOf(field);
  const sectionDone = section.fields.filter(done).length;
  const leftTitle = missing.length ? `Answer or mark TBD: ${missing.map((f) => f.label).join(', ')}` : '';
  const continueLabel = job.status === 'call_paused' ? 'Resume with Jessica →' : 'Continue with Jessica →';

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-gray-50 font-inter" data-testid="form-page">
      <header className="bg-white border-b border-gray-200 shrink-0">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <button onClick={() => navigate(MY_JOBS.pathname, { state: MY_JOBS.state })} className="text-xs text-gray-500 hover:text-brand-blue">← My jobs</button>
            <h1 className="text-lg font-black text-gray-900 truncate">{title || 'Add New Job'}</h1>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-500" data-testid="autosaved">{agoText(savedAt)}</span>
            <button onClick={async () => { await flush(); navigate(MY_JOBS.pathname, { state: MY_JOBS.state }); }}
              className="px-4 py-2 rounded-xl border border-gray-300 text-sm font-semibold text-gray-700 hover:border-brand-blue">Save &amp; exit</button>
          </div>
        </div>
      </header>

      <main className="flex-1 min-h-0 w-full max-w-7xl mx-auto px-6 py-4 grid grid-cols-1 lg:grid-cols-[290px_1fr] gap-6">
        <aside className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 min-h-0 flex flex-col" aria-label="Fields">
          <p className="text-sm font-bold text-gray-900" data-testid="progress">{progressText(progress)} complete</p>
          <div className="h-1.5 bg-gray-100 rounded-full mt-1.5 overflow-hidden">
            <div className="h-full bg-brand-green" style={{ width: `${Math.round((progress.done / progress.total) * 100)}%` }} />
          </div>
          <nav className="mt-3 flex-1 min-h-0 overflow-y-auto pr-1 space-y-2" data-testid="stepper">
            {sections.map((s) => {
              const n = s.fields.filter(done).length;
              return (
                <div key={s.key}>
                  <button onClick={() => goTo(s.fields.find((f) => !done(f)) || s.fields[0])} data-testid={`step-${s.key}`}
                    className={`w-full flex items-center gap-2 px-2 py-1 rounded-lg text-xs font-bold uppercase tracking-wide text-left ${s === section ? 'text-brand-blue' : 'text-gray-500 hover:text-gray-800'}`}>
                    <span className="flex-1">{s.label}</span><span>{n} / {s.fields.length}</span>
                  </button>
                  <ul>
                    {s.fields.map((f) => {
                      const a = answers[f.key];
                      const mark = a && a.status === 'tbd' ? 'TBD' : done(f) ? '✓' : '';
                      return (
                        <li key={f.key}>
                          <button onClick={() => goTo(f)} data-testid={`nav-${f.key}`} aria-current={f === field ? 'step' : undefined}
                            className={`w-full flex items-center gap-2 px-2 py-1 rounded-lg text-sm text-left ${f === field ? 'bg-blue-50 text-brand-blue font-semibold' : 'text-gray-700 hover:bg-gray-50'}`}>
                            <span className={`w-7 shrink-0 text-center text-[11px] font-bold rounded ${mark === '✓' ? 'text-brand-green' : mark === 'TBD' ? 'bg-amber-100 text-amber-800' : 'text-gray-300'}`}>{mark || '○'}</span>
                            <span className="truncate">{f.no}. {f.label}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </nav>
          <div className="mt-3 rounded-xl bg-teal-50 border border-teal-100 p-3 shrink-0">
            <p className="text-sm font-bold text-brand-dark">Next: 10–15 min call with Jessica</p>
            <p className="text-xs text-gray-600 mt-0.5">She'll fill any TBDs and ask about target companies and knock-outs.</p>
            {locked ? (
              <button onClick={() => navigate(`/jobs/intake/${intakeId}/review`)}
                className="mt-2 w-full px-3 py-2 rounded-lg bg-brand-blue text-white text-xs font-bold">Open review →</button>
            ) : (
              <>
                <span title={leftTitle} className="block mt-2">
                  <button onClick={continueWithJessica} disabled={!progress.complete || continuing} data-testid="continue-jessica"
                    className="w-full px-3 py-2 rounded-lg bg-brand-blue text-white text-xs font-bold disabled:opacity-40">{continueLabel}</button>
                </span>
                {missing.length > 0 && (
                  <p className="mt-2 text-xs text-gray-600" data-testid="fields-left">
                    {missing.length} field{missing.length > 1 ? 's' : ''} left: {missing[0].label} —{' '}
                    <button onClick={() => goTo(missing[0])} className="font-bold text-brand-blue hover:underline">Go to it</button>
                  </p>
                )}
                {progress.compensation_mismatch && (
                  <p className="mt-2 text-xs text-red-600">17. Compensation was entered for a different employment type — please re-enter it.</p>
                )}
              </>
            )}
          </div>
        </aside>

        <section className="min-h-0 flex flex-col gap-3" onKeyDown={onKeyDown}>
          <div className="shrink-0">
            <div className="flex items-baseline justify-between">
              <h2 className="text-xl font-black text-gray-900">{section.label}</h2>
              <span className="text-xs text-gray-500" data-testid="section-progress">Field {inSection + 1} of {section.fields.length} · {sectionDone} done</span>
            </div>
            <div className="h-1 bg-gray-200 rounded-full mt-1.5 overflow-hidden">
              <div className="h-full bg-brand-blue" style={{ width: `${Math.round((sectionDone / section.fields.length) * 100)}%` }} />
            </div>
          </div>
          <div className="flex-1 min-h-0 flex flex-col">
            <FieldCard key={field.key} field={field} draft={drafts[field.key] || {}} answer={answers[field.key]} state={saveState[field.key]}
              warnings={warnings[field.key]} ctx={ctx} locked={locked} payHint={field.key === 'compensation' ? payHint : ''}
              onChange={onChange(field)} onToggleTbd={toggleTbd(field)} />
          </div>
          <div className="shrink-0 flex items-center justify-between gap-3">
            <button onClick={() => setCursor(cursor - 1)} disabled={cursor === 0} data-testid="nav-back"
              className="px-4 py-2 rounded-xl border border-gray-300 bg-white text-sm font-semibold disabled:opacity-40">← Back</button>
            <span className="text-xs text-gray-400">{cursor + 1} of {flat.length} · Enter = Next</span>
            {!lastField ? (
              <button onClick={next} disabled={!canNext} data-testid="nav-next" title={canNext ? '' : 'Answer this field or mark it TBD'}
                className="px-5 py-2 rounded-xl bg-brand-blue text-white text-sm font-semibold disabled:opacity-40">Next →</button>
            ) : !locked ? (
              <span title={leftTitle}>
                <button onClick={continueWithJessica} disabled={!progress.complete || continuing} data-testid="continue-jessica-next"
                  className="px-5 py-2 rounded-xl bg-brand-blue text-white text-sm font-bold disabled:opacity-40">{continueLabel}</button>
              </span>
            ) : <span />}
          </div>
        </section>
      </main>
    </div>
  );
}

export default JobIntakeFormPage;
